import { format, isThisYear } from 'date-fns';
import { ExternalLink, Sparkles } from 'lucide-react';
import { useState } from 'react';

import {
  IntegrationRow,
  IntegrationStatus,
} from '@/modules/hiring-manager/components/IntegrationRow';
import { accessLabel } from '@/modules/mcp/access';
import { ClientTile } from '@/modules/mcp/components/clientTile/ClientTile';
import { useDisconnectMcpMutation } from '@/modules/mcp/mutations/useDisconnectMcpMutation';
import { useMcpConnectionsQuery } from '@/modules/mcp/queries/useMcpConnectionsQuery';
import { HELP_CENTER_URL } from '@/modules/support/support';
import { Button } from '@/modules/ui/components/button/Button';
import { ConfirmDialog } from '@/modules/ui/components/confirm-dialog/ConfirmDialog';
import type { components } from '@/types/apiSchema';
import { actionErrorMessage } from '@/utils/actionErrorMessage';

type OAuthConnection = components['schemas']['OAuthConnectionResponse'];

const connectedOn = (createdAt: string) => {
  const date = new Date(createdAt);
  return format(date, isThisYear(date) ? 'MMM d' : 'MMM d, yyyy');
};

/**
 * Finlay MCP: the AI apps (Claude, ChatGPT, IDEs) connected to the user's
 * account, each with a Disconnect. Connecting starts in the AI app, so the
 * row links to the how-to instead of offering a Connect button.
 */
export function McpIntegrationRow() {
  const connectionsQuery = useMcpConnectionsQuery();
  const disconnectMutation = useDisconnectMcpMutation();
  // Kept after closing so the dialog's copy holds while it fades out.
  const [disconnecting, setDisconnecting] = useState<OAuthConnection>();
  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);

  const connections = connectionsQuery.data ?? [];
  const status = connectionsQuery.isLoading
    ? 'Checking connections'
    : connectionsQuery.isError
      ? 'Status unavailable'
      : connections.length === 1
        ? '1 app connected'
        : connections.length > 1
          ? `${connections.length} apps connected`
          : 'Not connected';

  const openDisconnectDialog = (connection: OAuthConnection) => {
    // Reopening starts clean instead of showing the last attempt's error.
    disconnectMutation.reset();
    setDisconnecting(connection);
    setIsDisconnectDialogOpen(true);
  };

  return (
    <>
      <IntegrationRow
        action={
          <Button
            asChild
            className="h-8 rounded-md"
            size="sm"
            // Primary only once we know nothing is connected, so it doesn't flash.
            variant={
              connectionsQuery.isSuccess && connections.length === 0
                ? 'primary'
                : 'outline'
            }
          >
            <a
              href={`${HELP_CENTER_URL}/integrations/finlay-mcp/`}
              rel="noopener noreferrer"
              target="_blank"
            >
              How to connect
              <ExternalLink aria-hidden className="size-3.5" />
            </a>
          </Button>
        }
        description="Ask Claude, ChatGPT and other AI apps about your jobs, candidates and conversations. They can view them and add candidates to your jobs, but change nothing else."
        icon={Sparkles}
        name="Finlay MCP"
        status={
          <IntegrationStatus isActive={connections.length > 0}>
            {status}
          </IntegrationStatus>
        }
        usedBy="Jobs, candidates & conversations"
      >
        {connections.length > 0 && (
          <ul className="divide-border divide-y overflow-hidden rounded-[10px] border">
            {connections.map((connection) => (
              <li
                key={connection.id}
                className="flex items-center gap-3 px-3.5 py-3"
              >
                <ClientTile client={connection} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-medium">
                    {connection.clientName}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    Connected {connectedOn(connection.createdAt)} ·{' '}
                    {connection.redirectHost}
                  </p>
                </div>
                <span className="bg-muted text-muted-foreground hidden h-[22px] items-center rounded-full px-2 text-xs font-medium sm:inline-flex">
                  {accessLabel(connection.scopes)}
                </span>
                <Button
                  aria-label={`Disconnect ${connection.clientName}`}
                  className="h-7 rounded-md px-2.5 text-xs"
                  onClick={() => openDisconnectDialog(connection)}
                  size="sm"
                  variant="outline"
                >
                  Disconnect
                </Button>
              </li>
            ))}
          </ul>
        )}
      </IntegrationRow>

      <ConfirmDialog
        confirmText="Disconnect"
        confirming={disconnectMutation.isPending}
        confirmingText="Disconnecting…"
        description={`${disconnecting?.clientName} loses access to Finlay right away. To use it again, connect Finlay from ${disconnecting?.clientName}.`}
        error={
          disconnectMutation.isError
            ? actionErrorMessage(
                `disconnect ${disconnecting?.clientName}`,
                disconnectMutation.error,
              )
            : null
        }
        onConfirm={() =>
          disconnecting &&
          disconnectMutation.mutate(disconnecting.id, {
            onSuccess: () => setIsDisconnectDialogOpen(false),
          })
        }
        onOpenChange={(open) =>
          !disconnectMutation.isPending && setIsDisconnectDialogOpen(open)
        }
        open={isDisconnectDialogOpen}
        title={`Disconnect ${disconnecting?.clientName}?`}
        variant="destructive"
      />
    </>
  );
}
